import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SatelliteImageViewer from '../satellite/SatelliteImageViewer';
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Map as MapIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  Print as PrintIcon,
  Satellite as SatelliteIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line
} from 'recharts';

// Types for analysis results
interface AnalysisResult {
  id: string;
  analysisDate: string;
  commodity: string;
  region: string;
  configuration: {
    rules: string[];
    weights: { [key: string]: number };
    threshold: number;
  };
  results: {
    totalLocations: number;
    highPotentialLocations: number;
    averageScore: number;
    maxScore: number;
    averageConfidence: number;
    summary?: string;
    methodology?: string;
    analysisType?: 'polygon' | 'rectangular';
    regionInfo?: any; // Polygon or bounds information
  };
  locations: ScoringLocation[];
  highPotentialAreas: HighPotentialArea[];
  ruleContributions: RuleContribution[];
  performanceMetrics: PerformanceMetrics;
}

interface ScoringLocation {
  id: string;
  latitude: number;
  longitude: number;
  score: number;
  confidence: number;
  appliedRules: string[];
  ruleScores: { [ruleName: string]: number };
  classification: 'high' | 'moderate' | 'low';
}

interface HighPotentialArea {
  id: string;
  coordinates: { lat: number; lon: number };
  score: number;
  confidence: number;
  priorityRank: number;
  maxScore: number;
  meanScore: number;
  meanConfidence: number;
  recommendedAction: string;
  analysis_region?: string; // e.g., "Polygon Analysis (2829.3 km²)"
  geometry?: { type: string; coordinates: [number, number] };
}

interface RuleContribution {
  ruleName: string;
  displayName: string;
  weight: number;
  triggerCount: number;
  averageContribution: number;
  effectivenessScore: number;
}

interface PerformanceMetrics {
  precision: number;
  recall: number;
  f1Score: number;
  accuracy: number;
  rocAuc?: number;
  validationDate: string;
}

interface AlgorithmResultsViewerProps {
  results: AnalysisResult[];
  selectedResultId?: string;
  onResultSelect: (resultId: string) => void;
  onLocationClick: (location: ScoringLocation) => void;
  onAreaClick: (area: HighPotentialArea) => void;
  onExportResults: (resultId: string, format: string) => void;
}

// Debug logging utility for polygon analysis
const logPolygonAnalysisData = (result: AnalysisResult) => {
  if (result.results.analysisType === 'polygon') {
    // Log polygon region details
    if (result.results.regionInfo) {
      }
  }
};

const AlgorithmResultsViewer: React.FC<AlgorithmResultsViewerProps> = ({
  results,
  selectedResultId,
  onResultSelect,
  onLocationClick,
  onAreaClick,
  onExportResults
}) => {
  // State for UI controls
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<'score' | 'confidence' | 'distance'>('score');
  const [filterClassification, setFilterClassification] = useState<'all' | 'high' | 'moderate' | 'low'>('all');
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedCompareResults, setSelectedCompareResults] = useState<string[]>([]);

  // Get currently selected result
  const selectedResult = useMemo(() => {
    return results.find(r => r.id === selectedResultId);
  }, [results, selectedResultId]);

  // Debug logging for polygon analysis when selectedResult changes
  React.useEffect(() => {
    if (selectedResult) {
      logPolygonAnalysisData(selectedResult);
    }
  }, [selectedResult]);

  // Filter and sort locations
  const filteredLocations = useMemo(() => {
    if (!selectedResult) return [];
    
    let filtered = (selectedResult.highPotentialAreas || []).map(area => ({
      id: area.id,
      latitude: area.coordinates?.lat || 0,
      longitude: area.coordinates?.lon || 0,
      score: area.score || 0,
      confidence: area.confidence || 0,
      appliedRules: [],
      ruleScores: {},
      classification: (area.score >= 80 ? 'high' : area.score >= 60 ? 'moderate' : 'low') as 'high' | 'moderate' | 'low'
    }));
    
    // Apply classification filter
    if (filterClassification !== 'all') {
      filtered = filtered.filter(loc => loc.classification === filterClassification);
    }
    
    // Sort locations
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return b.score - a.score;
        case 'confidence':
          return b.confidence - a.confidence;
        default:
          return b.score - a.score;
      }
    });
    
    return filtered;
  }, [selectedResult, filterClassification, sortBy]);

  // Paginated locations
  const paginatedLocations = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLocations.slice(start, start + rowsPerPage);
  }, [filteredLocations, page, rowsPerPage]);

  // Chart data preparation
  const ruleContributionChartData = useMemo(() => {
    if (!selectedResult) return [];
    
    return (selectedResult.ruleContributions || [])
      .sort((a, b) => b.averageContribution - a.averageContribution)
      .map(rule => ({
        name: rule.displayName,
        contribution: rule.averageContribution,
        triggers: rule.triggerCount,
        effectiveness: rule.effectivenessScore
      }));
  }, [selectedResult]);

  const scoreDistributionData = useMemo(() => {
    if (!selectedResult) return [];
    
    const bins = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const distribution = bins.map(bin => ({
      range: `${bin}-${bin + 10}`,
      count: 0
    }));
    
    (selectedResult.highPotentialAreas || []).forEach(area => {
      const score = area.score || 0;
      const binIndex = Math.min(Math.floor(score / 10), 9);
      distribution[binIndex].count++;
    });
    
    return distribution.slice(0, -1); // Remove the last empty bin
  }, [selectedResult]);

  const performanceRadarData = useMemo(() => {
    if (!selectedResult?.performanceMetrics) return [];
    
    const metrics = selectedResult.performanceMetrics || {};
    return [
      {
        metric: 'Precision',
        value: metrics.precision * 100,
        fullMark: 100
      },
      {
        metric: 'Recall',
        value: metrics.recall * 100,
        fullMark: 100
      },
      {
        metric: 'F1 Score',
        value: metrics.f1Score * 100,
        fullMark: 100
      },
      {
        metric: 'Accuracy',
        value: metrics.accuracy * 100,
        fullMark: 100
      }
    ];
  }, [selectedResult]);

  // Handle export functionality
  const handleExport = useCallback((format: string) => {
    if (selectedResult) {
      onExportResults(selectedResult.id, format);
    }
  }, [selectedResult, onExportResults]);

  // Get score color
  const getScoreColor = useCallback((score: number): string => {
    if (score >= 70) return '#4CAF50';
    if (score >= 40) return '#FF9800';
    return 'var(--palantir-status-error)';
  }, []);

  // Get classification chip color
  const getClassificationColor = useCallback((classification: string) => {
    switch (classification) {
      case 'high': return 'success';
      case 'moderate': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  }, []);

  // Render results overview tab
  const renderOverviewTab = () => (
    <Box>
      {selectedResult && (
        <>
          {/* Polygon Analysis Context */}
          {selectedResult.results.analysisType === 'polygon' && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                '& .MuiAlert-icon': { color: '#0ea5e9' }
              }}
            >
              <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                <strong>Analysis Overview:</strong> Results generated for your custom polygon region.
                All metrics below reflect polygon-constrained analysis.
              </Typography>
            </Alert>
          )}
          
          {/* Results Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <Card sx={{
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '2px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" color="primary">
                    {selectedResult.results.totalLocations.toLocaleString()}
                  </Typography>
                  {selectedResult.results.analysisType === 'polygon' && (
                    <MapIcon sx={{ color: '#9333ea', fontSize: '1rem' }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  {selectedResult.results.analysisType === 'polygon' ? 'Polygon Locations Analyzed' : 'Total Locations Analyzed'}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '2px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" color="success.main">
                    {selectedResult.results.highPotentialLocations}
                  </Typography>
                  {selectedResult.results.analysisType === 'polygon' && (
                    <StarIcon sx={{ color: '#9333ea', fontSize: '1rem' }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  {selectedResult.results.analysisType === 'polygon' ? 'High-Potential Areas (Polygon)' : 'High-Potential Locations'}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '2px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" color="info.main">
                    {selectedResult.results.maxScore.toFixed(1)}
                  </Typography>
                  {selectedResult.results.analysisType === 'polygon' && (
                    <TrendingUpIcon sx={{ color: '#9333ea', fontSize: '1rem' }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  {selectedResult.results.analysisType === 'polygon' ? 'Peak Score (Polygon)' : 'Maximum Score'}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '2px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" color="warning.main">
                    {(selectedResult.results.averageConfidence * 100).toFixed(0)}%
                  </Typography>
                  {selectedResult.results.analysisType === 'polygon' && (
                    <AssessmentIcon sx={{ color: '#9333ea', fontSize: '1rem' }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  {selectedResult.results.analysisType === 'polygon' ? 'Polygon Confidence' : 'Average Confidence'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Charts Section */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
            {/* Rule Contributions Chart */}
            <Paper sx={{ 
              p: 2,
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '1px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">
                  Rule Contributions
                </Typography>
                {selectedResult.results.analysisType === 'polygon' && (
                  <Chip
                    label="Polygon Analysis"
                    size="small"
                    sx={{ 
                      backgroundColor: '#9333ea',
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ruleContributionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="contribution" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            {/* Score Distribution Chart */}
            <Paper sx={{ 
              p: 2,
              ...(selectedResult.results.analysisType === 'polygon' && {
                border: '1px solid #9333ea',
                backgroundColor: '#fefbff'
              })
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">
                  Score Distribution
                </Typography>
                {selectedResult.results.analysisType === 'polygon' && (
                  <Chip
                    label="Polygon Data"
                    size="small"
                    sx={{ 
                      backgroundColor: '#9333ea',
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={scoreDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Performance Metrics */}
          {selectedResult.performanceMetrics && Object.keys(selectedResult.performanceMetrics).length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Algorithm Performance
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Box>
                  <ResponsiveContainer width="100%" height={250}>
                    <RadarChart data={performanceRadarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis domain={[0, 100]} />
                      <Radar
                        name="Performance"
                        dataKey="value"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 1, color: 'var(--palantir-text-muted)' }}>
                    <strong>Precision:</strong> {(selectedResult.performanceMetrics.precision * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, color: 'var(--palantir-text-muted)' }}>
                    <strong>Recall:</strong> {(selectedResult.performanceMetrics.recall * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, color: 'var(--palantir-text-muted)' }}>
                    <strong>F1 Score:</strong> {(selectedResult.performanceMetrics.f1Score * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, color: 'var(--palantir-text-muted)' }}>
                    <strong>Accuracy:</strong> {(selectedResult.performanceMetrics.accuracy * 100).toFixed(1)}%
                  </Typography>
                  {selectedResult.performanceMetrics.rocAuc && (
                    <Typography variant="body1" sx={{ color: 'var(--palantir-text-muted)' }}>
                      <strong>ROC AUC:</strong> {selectedResult.performanceMetrics.rocAuc.toFixed(3)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );

  // Render locations table tab
  const renderLocationsTab = () => (
    <Box>
      {/* Polygon Analysis Alert */}
      {selectedResult?.results.analysisType === 'polygon' && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            backgroundColor: '#f8f4ff',
            border: '1px solid #9333ea',
            '& .MuiAlert-icon': { color: '#9333ea' }
          }}
        >
          <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
            <strong>Polygon Analysis:</strong> All locations below are constrained within your selected polygon region
            {selectedResult.results.regionInfo?.area_km2 && ` (${selectedResult.results.regionInfo.area_km2.toFixed(1)} km²)`}.
          </Typography>
        </Alert>
      )}
      
      {/* Filters and Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Classification</InputLabel>
          <Select
            value={filterClassification}
            onChange={(e) => setFilterClassification(e.target.value as any)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="high">High Potential</MenuItem>
            <MenuItem value="moderate">Moderate</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <MenuItem value="score">Score</MenuItem>
            <MenuItem value="confidence">Confidence</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          startIcon={<DownloadIcon />}
          onClick={() => handleExport('csv')}
          size="small"
          sx={{
            ...(selectedResult?.results.analysisType === 'polygon' && {
              borderColor: '#9333ea',
              color: '#9333ea',
              '&:hover': {
                backgroundColor: '#f8f4ff',
                borderColor: '#9333ea'
              }
            })
          }}
        >
          Export {selectedResult?.results.analysisType === 'polygon' ? 'Polygon ' : ''}CSV
        </Button>
      </Box>

      {/* Locations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Location</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">Confidence</TableCell>
              <TableCell align="center">Classification</TableCell>
              <TableCell align="center">Rules Applied</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLocations.map((location) => (
              <TableRow
                key={location.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onLocationClick(location)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedResult?.results.analysisType === 'polygon' && (
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#9333ea',
                          flexShrink: 0
                        }} 
                      />
                    )}
                    <Typography variant="body2">
                      {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <LinearProgress
                      variant="determinate"
                      value={location.score}
                      sx={{ 
                        width: 60, 
                        mr: 1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getScoreColor(location.score)
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 40 }}>
                      {location.score.toFixed(1)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {(location.confidence * 100).toFixed(0)}%
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={location.classification.toUpperCase()}
                    size="small"
                    color={getClassificationColor(location.classification) as any}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={location.appliedRules.join(', ')}
                  >
                    <Chip
                      label={location.appliedRules.length}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLocationClick(location);
                    }}
                  >
                    <MapIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredLocations.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );

  // Render high-potential areas tab
  const renderAreasTab = () => (
    <Box>
      {selectedResult?.highPotentialAreas.map((area) => (
        <Accordion key={area.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Area #{area.priorityRank}
              </Typography>
              {area.analysis_region && (
                <Chip
                  label={area.analysis_region}
                  size="small"
                  sx={{ 
                    mr: 1,
                    backgroundColor: '#f8f4ff',
                    color: '#9333ea',
                    border: '1px solid #9333ea'
                  }}
                />
              )}
              <Chip
                label={`Score: ${area.meanScore.toFixed(1)}`}
                size="small"
                color="primary"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Location Details
                </Typography>
                <Typography variant="body2">
                  <strong>Location:</strong> {area.coordinates.lat.toFixed(4)}°, {area.coordinates.lon.toFixed(4)}°
                </Typography>
                <Typography variant="body2">
                  <strong>Area:</strong> N/A
                </Typography>
                <Typography variant="body2">
                  <strong>Priority Rank:</strong> #{area.priorityRank}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Scoring Metrics
                </Typography>
                <Typography variant="body2">
                  <strong>Max Score:</strong> {area.maxScore.toFixed(1)}
                </Typography>
                <Typography variant="body2">
                  <strong>Mean Score:</strong> {area.meanScore.toFixed(1)}
                </Typography>
                <Typography variant="body2">
                  <strong>Confidence:</strong> {(area.meanConfidence * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recommended Action
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                {area.recommendedAction}
              </Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<MapIcon />}
                onClick={() => onAreaClick(area)}
              >
                View on Map
              </Button>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('geojson')}
              >
                Export GeoJSON
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  const renderSatelliteTab = () => {
    if (!selectedResult) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'var(--palantir-text-muted)' }}>
            No analysis results selected
          </Typography>
        </Paper>
      );
    }

    // Get coordinates from high potential areas or use default
    const defaultCoordinates = { lat: -29.5, lon: -70.5 }; // Default to Chilean Andes
    const hasHighPotentialAreas = selectedResult.highPotentialAreas && selectedResult.highPotentialAreas.length > 0;
    
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Satellite Imagery Analysis
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)', mb: 3 }}>
            View the actual Sentinel-2 satellite imagery used in this mineral discovery analysis.
            This high-resolution imagery provides detailed spectral data for detecting alteration minerals and geological features.
          </Typography>
          
          {selectedResult.results.analysisType === 'polygon' && hasHighPotentialAreas && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Polygon Analysis:</strong> The satellite imagery below shows each high-potential area identified within your selected polygon region. 
                Each area is analyzed for spectral signatures of mineral alteration including sericite, iron oxides, and clay minerals.
              </Typography>
            </Alert>
          )}
          
          {(selectedResult.results.analysisType !== 'polygon' || !hasHighPotentialAreas) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Spectral Analysis Context:</strong> The satellite imagery below shows the exact scene analyzed for 
                sericite alteration (SWIR1/SWIR2), iron oxides (Red/Blue), and clay minerals (SWIR1/NIR). 
                Areas with mineral alteration appear as brighter or differently colored zones.
              </Typography>
            </Alert>
          )}

          {/* Show satellite images for high potential areas (especially for polygon analysis) */}
          {selectedResult.results.analysisType === 'polygon' && hasHighPotentialAreas ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {selectedResult.highPotentialAreas.slice(0, 3).map((area, index) => (
                <Paper key={area.id} sx={{ p: 2, backgroundColor: '#f8f4ff', border: '1px solid #9333ea' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <StarIcon sx={{ color: '#9333ea' }} />
                    <Typography variant="h6" sx={{ color: '#9333ea' }}>
                      High-Potential Area #{area.priorityRank}
                    </Typography>
                    {area.analysis_region && (
                      <Chip
                        label={area.analysis_region}
                        size="small"
                        sx={{ 
                          backgroundColor: '#9333ea',
                          color: 'white'
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                      Coordinates: {area.coordinates.lat.toFixed(4)}°, {area.coordinates.lon.toFixed(4)}° | 
                      Score: {area.score.toFixed(1)} | Confidence: {(area.confidence * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                  <SatelliteImageViewer
                    latitude={area.coordinates.lat}
                    longitude={area.coordinates.lon}
                    analysisId={`${selectedResult.id}_area_${area.id}`}
                    onImageLoad={(scene) => {
                      }}
                  />
                </Paper>
              ))}
              {selectedResult.highPotentialAreas.length > 3 && (
                <Alert severity="info">
                  <Typography variant="body2">
                    Showing top 3 high-potential areas. Total areas found: {selectedResult.highPotentialAreas.length}
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <SatelliteImageViewer
              latitude={hasHighPotentialAreas ? selectedResult.highPotentialAreas[0].coordinates.lat : defaultCoordinates.lat}
              longitude={hasHighPotentialAreas ? selectedResult.highPotentialAreas[0].coordinates.lon : defaultCoordinates.lon}
              analysisId={selectedResult.id}
              onImageLoad={(scene) => {
                }}
            />
          )}
        </Paper>

        {/* Spectral Analysis Details */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Spectral Analysis Results
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Sericite Alteration
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Detection: Available in analysis results
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)' }}>
                  SWIR1/SWIR2 ratio analysis for phyllic alteration zones
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Iron Oxide Signature
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Strength: Available in analysis results
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Red/Blue ratio analysis for gossanous zones
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Clay Minerals
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Present: Available in analysis results
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)' }}>
                  SWIR1/NIR ratio analysis for argillic alteration
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Data Quality
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Level: High (Sentinel-2 MSI)
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)' }}>
                  Sentinel-2 MSI Level-2A surface reflectance
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      </Box>
    );
  };

  if (!selectedResult) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: 'var(--palantir-text-muted)' }}>
          No analysis results selected
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
          Select an analysis from the list to view detailed results
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2, m: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5">
                {selectedResult.commodity.toUpperCase()} Analysis Results
              </Typography>
              {selectedResult.results.analysisType === 'polygon' && (
                <Chip
                  label="Polygon Analysis"
                  size="small"
                  sx={{ 
                    backgroundColor: '#9333ea',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
              {selectedResult.region} • {new Date(selectedResult.analysisDate).toLocaleDateString()}
            </Typography>
            {selectedResult.results.methodology && (
              <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)', mt: 0.5 }}>
                {selectedResult.results.methodology}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<CompareIcon />}
              onClick={() => setCompareDialogOpen(true)}
              size="small"
            >
              Compare
            </Button>
            <Button
              startIcon={<ShareIcon />}
              size="small"
            >
              Share
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('pdf')}
              size="small"
              sx={{
                ...(selectedResult.results.analysisType === 'polygon' && {
                  borderColor: '#9333ea',
                  color: '#9333ea',
                  '&:hover': {
                    backgroundColor: '#f8f4ff',
                    borderColor: '#9333ea'
                  }
                })
              }}
            >
              Export {selectedResult.results.analysisType === 'polygon' ? 'Polygon ' : ''}PDF
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Polygon Analysis Information */}
      {selectedResult.results.analysisType === 'polygon' && selectedResult.results.regionInfo && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f4ff', border: '1px solid #9333ea' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MapIcon sx={{ color: '#9333ea' }} />
            <Typography variant="h6" sx={{ color: '#9333ea' }}>
              Analyzed Polygon Region
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            {selectedResult.results.regionInfo.area_km2 && (
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>Area</Typography>
                <Typography variant="h6" sx={{ color: 'var(--palantir-text-primary)' }}>{selectedResult.results.regionInfo.area_km2.toFixed(1)} km²</Typography>
              </Box>
            )}
            {selectedResult.results.regionInfo.perimeter_km && (
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>Perimeter</Typography>
                <Typography variant="h6" sx={{ color: 'var(--palantir-text-primary)' }}>{selectedResult.results.regionInfo.perimeter_km.toFixed(1)} km</Typography>
              </Box>
            )}
            {selectedResult.results.regionInfo.coordinates && (
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>Vertices</Typography>
                <Typography variant="h6" sx={{ color: 'var(--palantir-text-primary)' }}>{selectedResult.results.regionInfo.coordinates.length}</Typography>
              </Box>
            )}
            {selectedResult.results.regionInfo.bounds && (
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>Coverage</Typography>
                <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)' }}>
                  {selectedResult.results.regionInfo.bounds[0].toFixed(3)}°, {selectedResult.results.regionInfo.bounds[1].toFixed(3)}° to{' '}
                  {selectedResult.results.regionInfo.bounds[2].toFixed(3)}°, {selectedResult.results.regionInfo.bounds[3].toFixed(3)}°
                </Typography>
              </Box>
            )}
          </Box>
          {selectedResult.results.summary && (
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              {selectedResult.results.summary}
            </Typography>
          )}
        </Paper>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mx: 2, mb: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab 
            label={selectedResult.results.analysisType === 'polygon' ? 'Polygon Overview' : 'Overview'} 
            icon={selectedResult.results.analysisType === 'polygon' ? <MapIcon sx={{ color: '#9333ea' }} /> : <AssessmentIcon />} 
          />
          <Tab 
            label={`${selectedResult.results.analysisType === 'polygon' ? 'Polygon ' : ''}Locations (${selectedResult.results.totalLocations})`} 
            icon={<LocationIcon sx={{ ...(selectedResult.results.analysisType === 'polygon' && { color: '#9333ea' }) }} />} 
          />
          <Tab 
            label={`${selectedResult.results.analysisType === 'polygon' ? 'Polygon ' : ''}Areas (${(selectedResult.highPotentialAreas || []).length})`} 
            icon={<StarIcon sx={{ ...(selectedResult.results.analysisType === 'polygon' && { color: '#9333ea' }) }} />} 
          />
          <Tab 
            label={selectedResult.results.analysisType === 'polygon' ? 'Polygon Satellite' : 'Satellite Imagery'} 
            icon={<SatelliteIcon sx={{ ...(selectedResult.results.analysisType === 'polygon' && { color: '#9333ea' }) }} />} 
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ px: 2, pb: 2 }}>
        {currentTab === 0 && renderOverviewTab()}
        {currentTab === 1 && renderLocationsTab()}
        {currentTab === 2 && renderAreasTab()}
        {currentTab === 3 && renderSatelliteTab()}
      </Box>

      {/* Compare Results Dialog */}
      <Dialog open={compareDialogOpen} onClose={() => setCompareDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compare Analysis Results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--palantir-text-muted)', mb: 2 }}>
            Select up to 3 analysis results to compare performance metrics and outcomes.
          </Typography>
          {/* Comparison interface would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Compare Selected</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlgorithmResultsViewer;